import { isEqual } from 'lodash';
import { useLocalStorageContext } from 'providers';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';

import { isFn } from '@jebel/utils';

import { recordError } from 'shared/utils/record';

type SetState<S> = Dispatch<SetStateAction<S>>;

export function useLocalStorageState<S>(key: string): [S | undefined, SetState<S>];

export function useLocalStorageState<S>(key: string, fallback: S): [S, SetState<S>];

/**
 * Custom hook that provides a state value stored in the browser's local storage.
 * The state value is persisted across page reloads.
 *
 * @template S - The type of the state value.
 * @param key - The key used to store the state value in local storage.
 * @returns A tuple containing the state value and a function to update it.
 */
export function useLocalStorageState<S>(key: string, fallback?: S) {
  const { values, setValues } = useLocalStorageContext();

  const value = useMemo(() => {
    const stored = localStorage.getItem(key);

    if (stored) {
      try {
        return JSON.parse(stored) ?? fallback;
      } catch {
        recordError(`Failed to parse "${key}" from "localStorage" value, using fallback`);
      }
    }

    return values[key] ?? fallback;
  }, [values, key, fallback]);

  const setValue = useCallback(
    (value: S | undefined) => {
      setValues(state => {
        const prev = state[key] ?? fallback;

        if (isFn(value)) {
          value = value(prev);
        }

        if (value === undefined || value === null) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, JSON.stringify(value));
        }

        if (isEqual(prev, value)) {
          return state;
        }

        return { ...state, [key]: value };
      });
    },
    [key, setValues],
  );

  return [value, setValue];
}
